import React, { useCallback, useEffect, useState } from 'react';
import {
    Typography,
    Box,
    FormControl,
    TextField,
    InputLabel,
    FilledInput,
    InputAdornment,
    IconButton,
    createStyles,
    makeStyles,
    Theme,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';

import { loggingIn, login, loginFailed } from '€slices/auth';
import PrimaryButton from '€components/PrimaryButton';
import ContentLayout from '€layouts/ContentLayout';
import { Routes } from '€lib/routing';
import { user } from '€slices/user';
import MuiRouterLink from '€components/MuiRouterLink';

const useFilledFieldStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            '& .MuiInputBase-root': {
                backgroundColor: 'transparent',
                borderRadius: '8px',
                position: 'relative',
                border: '2px solid rgba(214, 221, 230, 1)',
            },
            '& .MuiInputBase-root:hover:not(.Mui-disabled)': {
                backgroundColor: 'transparent',
            },
            '& .MuiFilledInput-underline:hover:not(.Mui-disabled):before': {
                border: 'none',
            },
            '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
                border: 'none',
            },
            '& .MuiFilledInputBase-root.Mui-disabled.MuiInputBase-underline:before, .MuiInput-underline:before':
                {
                    borderColor: 'transparent',
                    transition: 'none',
                },
            '& .MuiInputLabel-filled': {
                transform: 'translate(12px, 22px) scale(1)',
                textTransform: 'uppercase',
                fontSize: '1rem',
                fontWeight: '700',
                lineHeight: '120%',
            },
            '& .MuiInputLabel-filled[data-shrink=true]': {
                transform: 'translate(12px, 12px) scale(0.85)',
            },
        },
        input: {
            '& .MuiFilledInput-input': {
                padding: '1.55rem 1.2rem',
            },
        },
        shrink: {
            '& .MuiInputLabel-shrink': {
                transform: 'translate(12px, 12px) scale(0.85)',
            },
        },
        submitButton: {
            boxShadow: ({ isDisabled }: SubmitButtonStyleProps) =>
                isDisabled ? 'none' : '0px 5px 15px 0px #0CBBF0',
        },
        link: {
            fontWeight: 700,
            letterSpacing: '0.0075em',
            borderBottom: `1px solid ${theme.palette.text.primary}`,
            marginLeft: '0.25rem',
            color: theme.palette.text.primary,
            textDecoration: 'none',
        },
    }),
);

type SubmitButtonStyleProps = {
    isDisabled: boolean;
};

interface FormValues {
    email: string;
    password: string;
}

const initialValues: FormValues = {
    email: '',
    password: '',
};

const LoginForm = () => {
    const {
        register,
        handleSubmit,
        setError,
        setValue,
        formState: { isValid },
    } = useForm({ mode: 'onChange' });
    const [showPassword, setShowPassword] = useState(false);
    const location = useLocation<{ from: { pathname: string } }>();
    const history = useHistory();
    const dispatch = useDispatch();
    const $user = useSelector(user);
    const loginError = useSelector(loginFailed);
    const loading = useSelector(loggingIn);
    const isDisabled = loading || !isValid;
    const classes = useFilledFieldStyles({ isDisabled });

    const handleClickShowPassword = () => {
        setShowPassword(previous => !previous);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const onSubmit = useCallback(
        async ({ username, password }) => {
            try {
                await dispatch(login({ username, password }));
            } catch (e) {
                setError('username', { type: 'manual', message: e.message });
            }
        },
        [dispatch, setError],
    );

    useEffect(() => {
        if ($user) {
            if (location?.state?.from) {
                history.replace(location.state.from);
            } else {
                window.location.replace(Routes.Dashboard);
            }
        }
    }, [$user, history, location, location.state]);

    useEffect(() => {
        if (loginError) {
            setValue('password', '');
        }
    }, [loginError, setValue]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ContentLayout.Form>
                <ContentLayout.FormFields>
                    <TextField
                        className={cx(classes.root, classes.shrink)}
                        required
                        error={loginError}
                        defaultValue={initialValues.email}
                        inputRef={register({ required: '* required' })}
                        type="email"
                        name="username"
                        label="User ID/Email"
                        variant="filled"
                    />
                    <FormControl className={classes.root} variant="filled">
                        <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                        <FilledInput
                            className={cx(classes.root, classes.shrink)}
                            required
                            error={loginError}
                            defaultValue={initialValues.password}
                            inputRef={register({ required: '* required' })}
                            name="password"
                            inputProps={{
                                className: classes.input,
                            }}
                            type={showPassword ? 'text' : 'password'}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </ContentLayout.FormFields>
                <Box mt="3.125rem" mb="2.5rem" display="flex" justifyContent="center">
                    <PrimaryButton
                        className={classes.submitButton}
                        size="medium"
                        type="submit"
                        disabled={isDisabled}
                    >
                        LOG IN
                    </PrimaryButton>
                </Box>

                <Box display="flex" justifyContent="center" textAlign="center">
                    <Typography variant="body1">Not an introducer yet?</Typography>
                    <a href={process.env.NEXT_PUBLIC_SIGN_UP} className={classes.link}>
                        Sign up
                    </a>
                </Box>
            </ContentLayout.Form>
        </form>
    );
};

export default LoginForm;
